import { useCallback, useEffect, useMemo, useRef } from 'react';

// Module-level count of Modal instances currently requesting the lock. Modals can nest (e.g. a
// "create tag" modal opened from inside a "create segment" modal) — each is its own `useModal`
// call with its own `enabled` ref, so the body scroll lock must be reference-counted rather than
// unconditionally released whenever any single instance closes, or closing the inner modal would
// un-hide scroll (and, with it, any stacking-order assumptions the still-open outer modal makes)
// while the outer one is still open.
let lockCount = 0;

const useScrollLock = (locked = false) => {
  const enabled = useRef(false);
  const scrollbarWidth = useMemo(() => `${window.innerWidth - document.body.clientWidth}px`, []);

  const lockElement = useCallback((el: HTMLElement) => {
    el.style.overflowY = 'hidden';
    el.style.paddingRight = scrollbarWidth;
    el.style.width = 'auto';
  }, [scrollbarWidth]);

  const unlockElement = useCallback((el: HTMLElement) => {
    el.style.overflowY = '';
    el.style.paddingRight = '';
    el.style.width = '';
  }, []);

  const enableLock = useCallback(() => {
    if (enabled.current) return;
    enabled.current = true;
    lockCount += 1;
    lockElement(document.body);
  }, [lockElement]);

  const disableLock = useCallback(() => {
    if (!enabled.current) return;
    enabled.current = false;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      unlockElement(document.body);
    }
  }, [unlockElement]);

  useEffect(() => {
    return () => {
      if (enabled.current) disableLock();
    };
  }, [disableLock]);

  return {
    lock: enableLock,
    locked: lockCount > 0,
    unlock: disableLock,
  };
};

export { useScrollLock };
export default useScrollLock;